import { useState } from "react";
import { motion } from "framer-motion";
import { mediaSrc } from "@/lib/api";
import api from "@/lib/api";
import { ArrowUpRight, Plus, Minus, Send, CalendarDays, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const embedUrl = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
};

function Hero({ d }) {
  const video = embedUrl(d.video_url);
  return (
    <section data-testid="public-hero-section" className="relative min-h-[92vh] flex items-end overflow-hidden">
      {d.background_image ? (
        <img src={mediaSrc(d.background_image)} alt={d.heading} className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-[#F2F2F0]" />
      )}
      {d.background_image && <div className="absolute inset-0 bg-black/30" />}
      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-12 pb-20">
        <motion.h1
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className={`font-display font-bold tracking-tighter leading-none text-5xl sm:text-6xl lg:text-7xl max-w-4xl ${d.background_image ? "text-white" : "text-[#0A0A0A]"}`}
        >
          {d.heading}
        </motion.h1>
        {d.subheading && (
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className={`font-body font-light text-lg lg:text-xl mt-6 max-w-xl ${d.background_image ? "text-white/85" : "text-[#555]"}`}
          >
            {d.subheading}
          </motion.p>
        )}
        {d.cta_label && d.cta_url && (
          <motion.a
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}
            href={d.cta_url} target="_blank" rel="noreferrer" data-testid="hero-cta"
            className="inline-flex items-center gap-2 mt-10 bg-[#0A0A0A] text-white px-7 py-3.5 rounded-full font-body font-medium hover:-translate-y-1 hover:shadow-xl transition-transform"
          >
            {d.cta_label} <ArrowUpRight className="w-4 h-4" />
          </motion.a>
        )}
      </div>
      {video && (
        <div className="hidden lg:block absolute bottom-20 right-12 z-20 w-[380px] aspect-video rounded-xl overflow-hidden shadow-2xl border border-white/20">
          <iframe src={video} title="video" className="w-full h-full" allowFullScreen />
        </div>
      )}
    </section>
  );
}

function About({ d }) {
  const right = d.image_position !== "left";
  return (
    <section data-testid="public-about-section" className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
      <div className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${right ? "" : "lg:[direction:rtl]"}`}>
        <Reveal className="lg:[direction:ltr]">
          <p className="font-body text-xs uppercase tracking-[0.3em] text-[#999] mb-5">{d.heading}</p>
          <div className="font-display text-2xl lg:text-3xl leading-snug text-[#0A0A0A] whitespace-pre-line">
            {d.body}
          </div>
        </Reveal>
        {d.image && (
          <Reveal delay={0.15} className="lg:[direction:ltr]">
            <div className="aspect-[4/5] overflow-hidden rounded-sm">
              <img src={mediaSrc(d.image)} alt={d.heading} className="w-full h-full object-cover" />
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}

function Gallery({ d }) {
  const items = d.items || [];
  return (
    <section data-testid="public-gallery-section" className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
      {d.heading && (
        <Reveal><h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mb-14">{d.heading}</h2></Reveal>
      )}
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [column-fill:balance]">
        {items.map((it, i) => {
          const inner = (
            <div className="group relative overflow-hidden rounded-sm">
              <img src={mediaSrc(it.image)} alt={it.caption || ""} className="w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              {it.caption && (
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-white font-body text-sm">{it.caption}</p>
                </div>
              )}
            </div>
          );
          return (
            <Reveal key={i} delay={(i % 3) * 0.08} className="mb-6 break-inside-avoid">
              {it.link ? <a href={it.link} target="_blank" rel="noreferrer" data-testid={`gallery-item-${i}`}>{inner}</a> : inner}
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

function Press({ d }) {
  const items = d.items || [];
  return (
    <section data-testid="public-press-section" className="bg-[#F2F2F0] py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {d.heading && <p className="font-body text-xs uppercase tracking-[0.3em] text-[#999] mb-12">{d.heading}</p>}
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-14">
          {items.map((it, i) => (
            <Reveal key={i} delay={(i % 2) * 0.1}>
              <figure>
                <blockquote className="font-display text-2xl lg:text-3xl leading-snug text-[#0A0A0A]">
                  “{it.quote}”
                </blockquote>
                <figcaption className="font-body text-sm text-[#555] mt-5 flex items-center gap-2">
                  — {it.source}
                  {it.url && <a href={it.url} target="_blank" rel="noreferrer" className="hover:text-black"><ExternalLink className="w-3.5 h-3.5" /></a>}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Faq({ d }) {
  const items = d.items || [];
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="public-faq-section" className="max-w-3xl mx-auto px-6 lg:px-12 py-24">
      {d.heading && <Reveal><h2 className="font-display text-4xl font-bold tracking-tight mb-12">{d.heading}</h2></Reveal>}
      <div className="divide-y divide-black/10 border-t border-black/10">
        {items.map((it, i) => (
          <div key={i} className="py-5">
            <button onClick={() => setOpen(open === i ? -1 : i)} data-testid={`faq-item-${i}`}
                    className="w-full flex items-center justify-between text-left gap-4">
              <span className="font-body font-medium text-lg">{it.question}</span>
              {open === i ? <Minus className="w-5 h-5 shrink-0" /> : <Plus className="w-5 h-5 shrink-0" />}
            </button>
            <motion.div initial={false} animate={{ height: open === i ? "auto" : 0, opacity: open === i ? 1 : 0 }}
                        transition={{ duration: 0.3 }} className="overflow-hidden">
              <p className="font-body font-light text-[#555] pt-4 leading-relaxed whitespace-pre-line">{it.answer}</p>
            </motion.div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    setSending(true);
    try {
      await api.post("/public/contact", form);
      toast.success("Thank you! Your message has been sent.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="public-contact-form" className="space-y-4">
      <input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
             placeholder="Your name" className="w-full bg-transparent border-b border-black/20 py-3 font-body focus:border-black outline-none transition-colors" />
      <input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
             placeholder="Your email" className="w-full bg-transparent border-b border-black/20 py-3 font-body focus:border-black outline-none transition-colors" />
      <textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Your message" rows={4} className="w-full bg-transparent border-b border-black/20 py-3 font-body focus:border-black outline-none transition-colors resize-none" />
      <button disabled={sending} data-testid="contact-submit"
              className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-7 py-3.5 rounded-full font-body font-medium hover:-translate-y-1 hover:shadow-xl transition-transform disabled:opacity-50">
        {sending ? "Sending..." : "Send message"} <Send className="w-4 h-4" />
      </button>
    </form>
  );
}

function Contact({ d }) {
  return (
    <section data-testid="public-contact-section" className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
      <div className="grid lg:grid-cols-2 gap-16">
        <Reveal>
          <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mb-8">{d.heading || "Contact"}</h2>
          <div className="space-y-3 font-body text-[#555]">
            {d.email && <p><a href={`mailto:${d.email}`} className="hover:text-black transition-colors">{d.email}</a></p>}
            {d.phone && <p>{d.phone}</p>}
            {d.address && <p className="whitespace-pre-line">{d.address}</p>}
          </div>
        </Reveal>
        {d.show_form && <Reveal delay={0.15}><ContactForm /></Reveal>}
      </div>
    </section>
  );
}

function NewsletterTeaser({ d }) {
  return (
    <section data-testid="public-newsletter-section" className="bg-[#0A0A0A] text-white py-24">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <Reveal>
          <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mb-4">{d.heading}</h2>
          {d.body && <p className="font-body font-light text-white/70 mb-8 max-w-xl mx-auto">{d.body}</p>}
          {d.cta_label && d.cta_url && (
            <a href={d.cta_url} target="_blank" rel="noreferrer" data-testid="newsletter-cta"
               className="inline-flex items-center gap-2 bg-white text-black px-7 py-3.5 rounded-full font-body font-medium hover:-translate-y-1 transition-transform">
              {d.cta_label} <ArrowUpRight className="w-4 h-4" />
            </a>
          )}
        </Reveal>
      </div>
    </section>
  );
}

function EventsTeaser({ d }) {
  const items = d.items || [];
  return (
    <section data-testid="public-events-section" className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
      <Reveal>
        <h2 className="font-display text-4xl font-bold tracking-tight mb-3">{d.heading || "Events"}</h2>
        {d.body && <p className="font-body font-light text-[#555] mb-10 max-w-xl">{d.body}</p>}
      </Reveal>
      <div className="space-y-px bg-black/10 border-y border-black/10">
        {items.map((it, i) => (
          <Reveal key={i} delay={(i % 3) * 0.06}>
            <a href={it.url || undefined} target={it.url ? "_blank" : undefined} rel="noreferrer" data-testid={`event-item-${i}`}
               className="bg-[#FCFCFA] flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-8 py-6 px-2 group hover:bg-[#F2F2F0] transition-colors">
              <CalendarDays className="w-5 h-5 text-[#999] shrink-0" />
              <span className="font-body text-sm text-[#555] sm:w-40">{it.date}</span>
              <span className="font-display text-xl font-medium flex-1">{it.title}</span>
              <span className="font-body text-sm text-[#555]">{it.location}</span>
            </a>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function BlogTeaser({ d }) {
  const items = d.items || [];
  return (
    <section data-testid="public-blog-section" className="bg-[#F2F2F0] py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <Reveal>
          <h2 className="font-display text-4xl font-bold tracking-tight mb-3">{d.heading || "Journal"}</h2>
          {d.body && <p className="font-body font-light text-[#555] mb-12 max-w-xl">{d.body}</p>}
        </Reveal>
        <div className="grid md:grid-cols-3 gap-8">
          {items.map((it, i) => (
            <Reveal key={i} delay={(i % 3) * 0.08}>
              <a href={it.url || undefined} target={it.url ? "_blank" : undefined} rel="noreferrer" data-testid={`blog-item-${i}`}
                 className="block bg-white p-8 h-full hover:-translate-y-1 transition-transform border border-black/5">
                <h3 className="font-display text-2xl font-bold mb-3">{it.title}</h3>
                <p className="font-body font-light text-[#555] leading-relaxed">{it.excerpt}</p>
                {it.url && <span className="inline-flex items-center gap-1 mt-4 font-body text-sm">Read more <ArrowUpRight className="w-3.5 h-3.5" /></span>}
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const MAP = {
  hero: Hero, about: About, gallery: Gallery, press: Press, faq: Faq,
  contact: Contact, newsletter_teaser: NewsletterTeaser,
  events_teaser: EventsTeaser, blog_teaser: BlogTeaser,
};

export default function BlockRenderer({ blocks = [] }) {
  return (
    <>
      {blocks.map((b) => {
        const C = MAP[b.type];
        if (!C) return null;
        return <C key={b.id} d={b.data || {}} />;
      })}
    </>
  );
}
